import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Flex,
  Select,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { OverviewMetrics, FeedbackData, ConversationMetric, Feedback, MessageContext } from '../types/dashboard';
import MessageContextModal from '../components/MessageContextModal';
import EditableSelect from '../components/EditableSelect';

const Dashboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [overview, setOverview] = useState<OverviewMetrics | null>(null);
  const [feedbackData, setFeedbackData] = useState<FeedbackData[]>([]);
  const [conversationMetrics, setConversationMetrics] = useState<ConversationMetric[]>([]);
  const [recentFeedback, setRecentFeedback] = useState<Feedback[]>([]);
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null);
  const [messageContext, setMessageContext] = useState<MessageContext[]>([]);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchDashboardData = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8889';
      const [overviewData, feedbackDist, convMetrics, feedback] = await Promise.all([
        fetch(`${baseUrl}/api/analytics/overview`).then(res => res.json()),
        fetch(`${baseUrl}/api/analytics/feedback-distribution?time_range=${timeRange}`).then(res => res.json()),
        fetch(`${baseUrl}/api/analytics/conversation-metrics?time_range=${timeRange}`).then(res => res.json()),
        fetch(`${baseUrl}/api/analytics/recent-feedback`).then(res => res.json()),
      ]);

      setOverview(overviewData);
      setFeedbackData(feedbackDist);
      setConversationMetrics(convMetrics);
      setRecentFeedback(feedback);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatSatisfactionRate = (rate: number | undefined | null): string => {
    if (rate === undefined || rate === null) return '0%';
    return `${(rate * 100).toFixed(1)}%`;
  };

  const formatResponseTime = (time: number | undefined | null): string => {
    if (time === undefined || time === null) return '0s';
    return `${(time / 1000).toFixed(2)}s`;
  };

  const handleViewContext = async (messageId: number) => {
    setSelectedMessageId(messageId);
    setIsLoadingContext(true);
    onOpen();
    
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8889';
      const response = await fetch(`${baseUrl}/api/analytics/message-context/${messageId}?context_size=5`);
      const data = await response.json();
      setMessageContext(data);
    } catch (error) {
      console.error('Error fetching message context:', error);
      setMessageContext([]);
    } finally {
      setIsLoadingContext(false);
    }
  };

  const handleStatusChange = async (messageId: number, status: string) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8889';
      const response = await fetch(`${baseUrl}/api/analytics/feedback/${messageId}/status?status=${status}`, {
        method: 'PUT',
      });
      
      if (response.ok) {
        // Update the local state to reflect the change
        setRecentFeedback(prevFeedback => 
          prevFeedback.map(feedback => 
            feedback.id === messageId 
              ? { ...feedback, feedback_status: status } 
              : feedback
          )
        );
      } else {
        console.error('Failed to update feedback status');
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
    }
  };

  return (
    <Box p={8}>
      <Flex justify="space-between" align="center" mb={8}>
        <Heading size="lg">Dashboard</Heading>
        <Select
          w="200px"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </Select>
      </Flex>

      {/* Overview Stats */}
      <Grid templateColumns="repeat(4, 1fr)" gap={6} mb={8}>
        <StatCard
          label="Total Conversations"
          value={overview?.total_conversations ?? 0}
          helpText="Last 30 days"
        />
        <StatCard
          label="Total Users"
          value={overview?.total_users ?? 0}
          helpText="Unique users"
        />
        <StatCard
          label="Satisfaction Rate"
          value={formatSatisfactionRate(overview?.satisfaction_rate)}
          helpText="Based on feedback"
        />
        <StatCard
          label="Avg Response Time"
          value={formatResponseTime(overview?.avg_response_time)}
          helpText="Time to respond"
        />
      </Grid>

      {/* Charts */}
      <Grid templateColumns="repeat(2, 1fr)" gap={6} mb={8}>
        <Card>
          <CardHeader>
            <Heading size="md">Feedback Distribution</Heading>
          </CardHeader>
          <CardBody>
            <Box h="300px">
              <ResponsiveContainer>
                <LineChart data={feedbackData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="positive" stroke="#48BB78" />
                  <Line type="monotone" dataKey="negative" stroke="#F56565" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <Heading size="md">Conversation Metrics</Heading>
          </CardHeader>
          <CardBody>
            <Box h="300px">
              <ResponsiveContainer>
                <BarChart data={conversationMetrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4299E1" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>
      </Grid>

      {/* Recent Feedback Table */}
      <Card>
        <CardHeader>
          <Heading size="md">Recent Feedback</Heading>
        </CardHeader>
        <CardBody>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Date</Th>
                <Th>User</Th>
                <Th>Message</Th>
                <Th>Rating</Th>
                <Th>Feedback</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {recentFeedback.map((feedback) => (
                <Tr key={feedback.id}>
                  <Td>{new Date(feedback.created_at).toLocaleDateString()}</Td>
                  <Td>{feedback.first_name && feedback.last_name 
                      ? `${feedback.first_name} ${feedback.last_name}` 
                      : `User ${feedback.user_id}`}</Td>
                  <Td noOfLines={2}>{feedback.content}</Td>
                  <Td>
                    <Badge
                      colorScheme={feedback.rating === 1 ? 'green' : 'red'}
                    >
                      {feedback.rating === 1 ? 'Positive' : 'Negative'}
                    </Badge>
                  </Td>
                  <Td>{feedback.feedback_text || '-'}</Td>
                  <Td>
                    <EditableSelect
                      value={feedback.feedback_status || 'New'}
                      options={[
                        { value: 'New', label: 'New' },
                        { value: 'In Review', label: 'In Review' },
                        { value: 'Resolved', label: 'Resolved' },
                        { value: "Won't Fix", label: "Won't Fix" }
                      ]}
                      onChange={(value) => handleStatusChange(feedback.id, value)}
                      size="sm"
                    />
                  </Td>
                  <Td>
                    <Button 
                      size="sm" 
                      colorScheme="blue" 
                      onClick={() => handleViewContext(feedback.id)}
                    >
                      View Context
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Message Context Modal */}
      <MessageContextModal
        isOpen={isOpen}
        onClose={onClose}
        messageContext={messageContext}
        isLoading={isLoadingContext}
      />
    </Box>
  );
};

const StatCard: React.FC<{ 
  label: string; 
  value: string | number; 
  helpText: string 
}> = ({
  label,
  value,
  helpText,
}) => (
  <Card>
    <CardBody>
      <Stat>
        <StatLabel>{label}</StatLabel>
        <StatNumber>{value}</StatNumber>
        <StatHelpText>{helpText}</StatHelpText>
      </Stat>
    </CardBody>
  </Card>
);

export default Dashboard; 